import React, { Children, cloneElement } from 'react';

import ReactDOM from 'react-dom';

import { bool, element, number, shape, oneOf, string } from 'prop-types';

import ClassNames from 'classnames';
import { capitalize, getRectCoordinates, isDescendant } from '../../../utils';

import BaseComponent from '../BaseComponent';
import Portal from '../portal/Portal';

const POSITIONS = [
  'top',
  'top-left',
  'top-right',
  'bottom',
  'bottom-left',
  'bottom-right',
  'right',
  'right-top',
  'right-bottom',
  'left',
  'left-top',
  'left-bottom'
];

export default class Trigger extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      active: false,
      position: 'top',
      visible: false,
      x: 0,
      y: 0
    };

    this.triggerElement = React.createRef();
    this.popupElement = React.createRef();
    this.delayDisplayTimeOut = null;
  }

  componentDidMount() {
    const { action } = this.props;
    if (action === 'programmatic') {
      this.handleProgrammatic();
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.pastShow(this.triggerElement.current.getBoundingClientRect());
    });
  }

  componentDidUpdate(prevProps) {
    const { action, overrideVisible } = this.props;
    const { visible } = this.state;

    if (typeof overrideVisible === 'undefined') return;

    if (
      action === 'click' &&
      (visible === false || overrideVisible === false) &&
      typeof document !== 'undefined'
    ) {
      document.removeEventListener('click', this.handleClickOut, true);
    }

    if (
      action === 'programmatic' &&
      overrideVisible !== prevProps.overrideVisible
    ) {
      this.handleProgrammatic();
    }

    if (visible !== overrideVisible) {
      // This was written before the team adopted its ESLint rules more
      // strictly. A proper fix may require a nontrivial refactor.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ visible: overrideVisible, active: overrideVisible });
    }

    if (this.popupElement.current) {
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(this.popupElement.current);
    }
  }

  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }

  fireEvents(type, e) {
    const childCallback = this.props.children.props[type];

    if (childCallback) {
      childCallback(e);
    }

    const callback = this.props[type];

    if (callback) {
      callback(e);
    }
  }

  handleClickOut = e => {
    const { triggerElement, popupElement, handleClickOut } = this;
    // This was written before the team adopted its ESLint rules more
    // strictly. A proper fix may require a nontrivial refactor.
    /* eslint-disable react/no-find-dom-node */
    if (e.target === ReactDOM.findDOMNode(triggerElement.current)) {
      return;
    }
    if (isDescendant(ReactDOM.findDOMNode(triggerElement.current), e.target)) {
      return;
    }
    if (e.target === ReactDOM.findDOMNode(popupElement.current)) {
      return;
    }
    if (isDescendant(ReactDOM.findDOMNode(popupElement.current), e.target)) {
      return;
    }
    /* eslint-enable react/no-find-dom-node */
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', handleClickOut, true);
    }
    this.hide();
  };

  handleOnClick = e => {
    this.fireEvents('onClick', e);

    const { visible } = this.state;
    const { handleClickOut } = this;

    if (typeof document !== 'undefined') {
      if (!visible) {
        document.addEventListener('click', handleClickOut, true);
      } else {
        document.removeEventListener('click', handleClickOut, true);
      }
    }

    const el = e.currentTarget;

    if (el !== null) {
      const rect = el.getBoundingClientRect();

      clearTimeout(this.delayDisplayTimeout);

      if (visible) {
        this.hide();
      } else {
        this.setState({ visible: true });
        this.show(rect);
      }
    }
  };

  handleProgrammatic = () => {
    const { active } = this.state;

    if (active) {
      this.hide();
    } else {
      this.show(this.triggerElement.current.getBoundingClientRect());
    }
  };

  handlePopupMouseOver = () => {
    clearTimeout(this.hoverTimeout);
  };

  handlePopupMouseLeave = () => {
    this.hide();
  };

  handleOnMouseLeave = e => {
    this.fireEvents('onMouseLeave', e);

    clearTimeout(this.delayDisplayTimeout);
    this.hoverTimeout = setTimeout(() => this.hide(), 100);
  };

  handleOnMouseOver = e => {
    clearTimeout(this.hoverTimeout);
    this.fireEvents('onMouseOver ', e);

    if (this.state.active) {
      return;
    }

    const { mouseEnterDelay } = this.props;

    const el = e.currentTarget;

    if (el !== null) {
      const rect = el.getBoundingClientRect();

      this.setState({ active: true });
      clearTimeout(this.delayDisplayTimeout);

      this.delayDisplayTimeout = setTimeout(() => {
        this.show(rect);
      }, mouseEnterDelay);
    }
  };

  hide() {
    if (this.props.action === 'click' && typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleClickOut, true);
    }

    this.setState({ visible: false, active: false });
    const { onChange } = this.props;

    if (onChange) {
      onChange(false);
    }
  }

  pastShow(hoverRect) {
    const { autoAdjust, position } = this.props;
    // This was written before the team adopted its ESLint rules more
    // strictly. A proper fix may require a nontrivial refactor.
    /* eslint-disable-next-line react/no-find-dom-node */
    const ttNode = ReactDOM.findDOMNode(this);
    if (ttNode !== null) {
      const ttRect = ttNode.getBoundingClientRect();

      const docWidth =
        typeof document !== 'undefined' && document.documentElement.clientWidth;
      const docHeight =
        typeof document !== 'undefined' &&
        document.documentElement.clientHeight;

      const pCoordinates = getRectCoordinates(ttRect, hoverRect);

      const parsePositionKey = str => {
        const words = str.split('-');
        return `${words[0]}${words[1] ? capitalize(words[1]) : ''}`;
      };

      const currentPos = pCoordinates[parsePositionKey(position)];

      const newState = { position, x: currentPos.x, y: currentPos.y };

      if (autoAdjust) {
        let tmpCurrentPos = currentPos;
        let tmpPosition = newState.position;
        let loopIndex = 0;
        let flag = true;

        // TODO: This can be improve
        while (flag) {
          const topOutOfBound = tmpCurrentPos.y < 0;
          const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
          const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;

          const bottomOutOfBound =
            tmpCurrentPos.y + ttRect.height > scrollY + docHeight;

          const rightOutOfBound =
            tmpCurrentPos.x + ttRect.width > scrollX + docWidth;

          const leftOutOfBound = tmpCurrentPos.x < 0;

          const outOfBounds =
            topOutOfBound ||
            bottomOutOfBound ||
            rightOutOfBound ||
            leftOutOfBound;

          if (!outOfBounds) {
            newState.position = tmpPosition;
            newState.x = tmpCurrentPos.x;
            newState.y = tmpCurrentPos.y;
            flag = false;
          } else if (loopIndex < currentPos.nextPlacements.length) {
            tmpCurrentPos = pCoordinates[currentPos.nextPlacements[loopIndex]];
            tmpPosition = tmpCurrentPos.name;
          } else {
            flag = false;
          }

          loopIndex += 1;
        }
      }

      this.setState(newState);
    }
  }

  show(hoverReact) {
    this.setState({ visible: true }, this.pastShow.bind(this, hoverReact));
    const { onChange } = this.props;

    if (onChange) {
      onChange(true);
    }

    this.pastShow(hoverReact);
  }

  render() {
    const { position, visible, x, y } = this.state;
    const {
      action,
      children,
      id,
      overlayClassName,
      overlayStyle,
      popup,
      overrideVisible
    } = this.props;

    const newChildProps = {};

    let trueVisible = visible;

    if (typeof overrideVisible !== 'undefined') {
      trueVisible = overrideVisible;
    }

    const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const style = {
      left: `${x + scrollX}px`,
      top: `${y + scrollY}px`
    };

    const classNames = {};

    if (position !== null && position !== 'none') {
      classNames[position] = true;
    }

    const {
      handlePopupMouseOver,
      handlePopupMouseLeave,
      handleOnMouseLeave,
      handleOnMouseOver,
      handleOnClick,
      triggerElement,
      popupElement
    } = this;

    const portalHoverEvents = {};

    if (action === 'hover') {
      newChildProps.onMouseLeave = handleOnMouseLeave;
      newChildProps.onMouseOver = handleOnMouseOver;
      portalHoverEvents.onMouseLeave = handlePopupMouseLeave;
      portalHoverEvents.onMouseOver = handlePopupMouseOver;
    }

    if (action === 'click') {
      newChildProps.onClick = handleOnClick;
    }

    const childrenWithProps = Children.only(
      cloneElement(children, { ...newChildProps, ref: triggerElement })
    );

    const popupWithProps = cloneElement(popup, {
      ref: popupElement
    });

    return (
      <>
        {trueVisible ? (
          <Portal>
            <div
              id={id}
              style={{
                ...style,
                ...overlayStyle,
                position: 'absolute'
              }}
              {...portalHoverEvents}
              className={ClassNames([overlayClassName, position])}
            >
              {popupWithProps}
            </div>
          </Portal>
        ) : null}
        {childrenWithProps}
      </>
    );
  }
}

Trigger.propTypes = {
  action: oneOf(['hover', 'click', 'programmatic']),
  autoAdjust: bool,
  children: element.isRequired,
  className: string,
  content: string,
  id: string,
  mouseEnterDelay: number,
  overrideVisible: bool,
  position: oneOf(POSITIONS),
  style: shape({})
};

Trigger.defaultProps = {
  action: 'hover',
  autoAdjust: false,
  className: undefined,
  children: null,
  content: '',
  id: undefined,
  mouseEnterDelay: 0,
  overrideVisible: undefined,
  position: 'right',
  style: {}
};
